/**
 * Products list route (Organizers only)
 * Manage the food products that compose dishes
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useProducts,
  useCreateProduct,
  useUpdateProduct,
  useDeleteProduct,
  useOffSearch,
} from '@/hooks/useProducts';
import { requireOrganizer } from '@/lib/auth/routeGuards';
import { Loading } from '@/components/ui/Loading';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { EmptyState } from '@/components/ui/EmptyState';
import { ProductForm } from '@/components/products/ProductForm';
import { useToast } from '@/components/ui/ToastProvider';
import type {
  Product,
  CreateProductRequest,
  OffProduct,
} from '@/types/api';

export const Route = createFileRoute('/products/')({
  beforeLoad: requireOrganizer,
  component: ProductsComponent,
});

function formatGram(value?: number): string {
  if (value === undefined || value === null) return '—';
  return `${Math.round(value * 10) / 10} g`;
}

function ProductsComponent() {
  const { t } = useTranslation();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const { data, isLoading, error } = useProducts({ query: search || undefined, limit: 100 });
  const products = data?.products ?? [];

  const [formMode, setFormMode] = useState<'none' | 'create' | 'edit'>('none');
  const [editing, setEditing] = useState<Product | null>(null);

  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();

  const [offQuery, setOffQuery] = useState('');
  const [offEnabled, setOffEnabled] = useState(false);
  const offSearch = useOffSearch(offQuery, offEnabled);

  const handleCreate = async (payload: CreateProductRequest) => {
    try {
      await createProduct.mutateAsync(payload);
      toast.success(t('products.created'));
      setFormMode('none');
    } catch {
      toast.error(t('products.errorSaving'));
    }
  };

  const handleUpdate = async (payload: CreateProductRequest) => {
    if (!editing) return;
    try {
      await updateProduct.mutateAsync({ id: editing.id, data: payload });
      toast.success(t('products.updated'));
      setFormMode('none');
      setEditing(null);
    } catch {
      toast.error(t('products.errorSaving'));
    }
  };

  const handleDelete = async (product: Product) => {
    if (!window.confirm(t('products.deleteConfirm', { name: product.name })))
      return;
    try {
      await deleteProduct.mutateAsync(product.id);
      toast.success(t('products.deleted'));
    } catch {
      toast.error(t('products.errorDeleting'));
    }
  };

  const handleToggleFavorite = async (product: Product) => {
    try {
      await updateProduct.mutateAsync({
        id: product.id,
        data: { isFavorite: !product.isFavorite },
      });
    } catch {
      toast.error(t('products.errorSaving'));
    }
  };

  const handleImport = async (off: OffProduct) => {
    try {
      await createProduct.mutateAsync({
        barcode: off.barcode,
        name: off.name,
        brand: off.brand,
        imageUrl: off.imageUrl,
        energyKcal100g: off.energyKcal100g,
        proteins100g: off.proteins100g,
        carbohydrates100g: off.carbohydrates100g,
        fat100g: off.fat100g,
        fiber100g: off.fiber100g,
        servingSize: off.servingSize,
        source: 'openfoodfacts',
      });
      toast.success(t('products.imported'));
    } catch {
      toast.error(t('products.alreadyImported'));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('products.title')}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              {t('products.description')}
            </p>
          </div>
          <button
            onClick={() => {
              setEditing(null);
              setFormMode(formMode === 'create' ? 'none' : 'create');
            }}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
          >
            + {t('products.addProduct')}
          </button>
        </div>

        {formMode !== 'none' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">
              {formMode === 'create' ? t('products.addProduct') : t('products.editProduct')}
            </h2>
            <ProductForm
              initial={formMode === 'edit' ? editing ?? undefined : undefined}
              submitting={createProduct.isPending || updateProduct.isPending}
              onSubmit={formMode === 'create' ? handleCreate : handleUpdate}
              onCancel={() => {
                setFormMode('none');
                setEditing(null);
              }}
              submitLabel={formMode === 'create' ? t('products.addProduct') : t('app.save')}
            />
          </div>
        )}

        {/* OpenFoodFacts import */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-base font-semibold text-gray-900 mb-3">
            {t('products.importFromOff')}
          </h2>
          <div className="flex gap-2">
            <input
              type="text"
              value={offQuery}
              onChange={(e) => setOffQuery(e.target.value)}
              placeholder={t('products.searchOffPlaceholder')}
              className="block flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
            <button
              onClick={() => setOffEnabled(true)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-800 rounded-lg hover:bg-gray-900"
            >
              {t('products.searchOff')}
            </button>
          </div>

          {offSearch.isFetching && <Loading />}

          {offSearch.data && offSearch.data.products.length === 0 && (
            <p className="mt-3 text-sm text-gray-500">
              {t('products.noOffResults')}
            </p>
          )}

          {offSearch.data && offSearch.data.products.length > 0 && (
            <ul className="mt-4 divide-y divide-gray-100 max-h-80 overflow-y-auto">
              {offSearch.data.products.map((off) => (
                <li
                  key={`${off.barcode}-${off.name}`}
                  className="flex items-center justify-between py-2"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {off.name}
                      {off.brand ? ` — ${off.brand}` : ''}
                    </p>
                    <p className="text-xs text-gray-500">
                      {off.energyKcal100g ?? '—'} kcal · P{' '}
                      {formatGram(off.proteins100g)} · G{' '}
                      {formatGram(off.carbohydrates100g)} · L{' '}
                      {formatGram(off.fat100g)}
                    </p>
                  </div>
                  <button
                    onClick={() => handleImport(off)}
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
                  >
                    {t('products.import')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('products.searchPlaceholder')}
          className="block w-full sm:w-96 mb-6 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        />

        {isLoading && <Loading />}

        {error && (
          <ErrorMessage
            title={t('products.errorLoading')}
            message={error instanceof Error ? error.message : t('errors.generic')}
          />
        )}

        {!isLoading && !error && products.length === 0 && (
          <EmptyState
            title={t('products.noProducts')}
            description={t('products.noProductsDescription')}
          />
        )}

        {products.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('products.name')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('products.per100g')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    {t('products.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {products.map((product) => (
                  <tr key={product.id}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleFavorite(product)}
                          className={
                            product.isFavorite
                              ? 'text-yellow-500'
                              : 'text-gray-300 hover:text-yellow-500'
                          }
                          aria-label={t('products.favorite')}
                        >
                          ★
                        </button>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {product.name}
                          </p>
                          {product.brand && (
                            <p className="text-xs text-gray-500">
                              {product.brand}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {product.energyKcal100g ?? '—'} kcal · P{' '}
                      {formatGram(product.proteins100g)} · G{' '}
                      {formatGram(product.carbohydrates100g)} · L{' '}
                      {formatGram(product.fat100g)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm whitespace-nowrap">
                      <button
                        onClick={() => {
                          setEditing(product);
                          setFormMode('edit');
                        }}
                        className="font-medium text-gray-600 hover:text-gray-900"
                      >
                        {t('common.edit')}
                      </button>
                      <button
                        onClick={() => handleDelete(product)}
                        className="ml-4 font-medium text-red-600 hover:text-red-800"
                      >
                        {t('common.delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
